import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Container,
  Fade,
  Slide,
  TextField,
} from '@mui/material';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import logo from '../assets/logo.png';
import checklistLottie from '../assets/checklist.lottie';
import { authApi } from '../lib/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState({ type: 'info', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGuestSubmitting, setIsGuestSubmitting] = useState(false);

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
    return '';
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleGetStarted = async () => {
    const nextError = validateEmail(email);
    setEmailError(nextError);

    if (nextError) {
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: '' });

    try {
      await authApi.post('/send-otp', { email: email.trim() });
      sessionStorage.setItem('lanzo-signup-email', email.trim());
      sessionStorage.removeItem('lanzo-signup-token');
      navigate('/signup', {
        state: {
          email: email.trim(),
        },
      });
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to start signup right now';

      if (/user already exists/i.test(message)) {
        navigate('/login');
        return;
      }

      setStatus({ type: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuestContinue = async () => {
    setIsGuestSubmitting(true);
    setStatus({ type: 'info', message: '' });

    try {
      const response = await authApi.post('/guest-login');
      localStorage.setItem('lanzo-token', response.data?.token || '');
      sessionStorage.removeItem('lanzo-signup-token');
      sessionStorage.setItem('lanzo-signup-email', 'guest@lanzo.local');
      navigate('/app');
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to start a guest session right now';
      setStatus({ type: 'error', message });
    } finally {
      setIsGuestSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Fade in={mounted} timeout={600}>
        <Box
          component="header"
          sx={{
            bgcolor: '#ffffff',
            px: { xs: 3, md: 6 },
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 0 #E2E8F0',
            zIndex: 10,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src={logo}
              alt="Lanzo logo"
              sx={{ width: 36, height: 36, objectFit: 'contain' }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.3px',
                color: '#2563EB',
                lineHeight: 1,
              }}
            >
              Lanzo
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              href="/login"
              sx={{
                borderColor: '#2563EB',
                color: '#2563EB',
                fontWeight: 700,
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem',
                transition: 'all 0.22s ease',
                '&:hover': {
                  bgcolor: '#EFF6FF',
                  borderColor: '#1D4ED8',
                  color: '#1D4ED8',
                },
              }}
            >
              Log in
            </Button>
            <Button
              variant="contained"
              onClick={handleGuestContinue}
              disabled={isGuestSubmitting}
              sx={{
                bgcolor: '#2563EB',
                color: '#ffffff',
                fontWeight: 700,
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem',
                boxShadow: '0 4px 16px rgba(37,99,235,0.35)',
                transition: 'all 0.22s ease',
                '&:hover': {
                  bgcolor: '#1D4ED8',
                  boxShadow: '0 6px 24px rgba(37,99,235,0.45)',
                },
              }}
            >
              {isGuestSubmitting ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : 'Guest'}
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          background: 'linear-gradient(160deg, #EFF6FF 0%, #DBEAFE 100%)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid
            container
            spacing={{ xs: 4, md: 8 }}
            alignItems="center"
          >
            {/* ── Left: copy + CTAs ──────────────────────────────── */}
            <Grid item xs={12} md={6}>
              <Slide in={mounted} direction="right" timeout={700}>
                <Box>
                  <Fade in={mounted} timeout={900}>
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#2563EB',
                          fontWeight: 700,
                          letterSpacing: 2,
                          fontSize: '0.75rem',
                        }}
                      >
                        Task management, reimagined
                      </Typography>

                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 800,
                          color: '#0F172A',
                          lineHeight: 1.15,
                          mt: 1.5,
                          mb: 2.5,
                          fontSize: { xs: '2.2rem', md: '3rem' },
                          letterSpacing: '-1px',
                        }}
                      >
                        Organize your work.{' '}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#475569',
                          fontSize: '1.05rem',
                          lineHeight: 1.75,
                          mb: 4,
                          maxWidth: 440,
                        }}
                      >
                        Lanzo helps you capture tasks, track progress, and stay
                        focused — all in one clean, intuitive workspace.
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 440 }}>
                        <TextField
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setEmailError(validateEmail(e.target.value));
                          }}
                          onClick={() => setEmailError(validateEmail(email))}
                          onBlur={() => setEmailError(validateEmail(email))}
                          error={!!emailError}
                          helperText={emailError}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '50px',
                              backgroundColor: '#ffffff',
                              '& fieldset': {
                                borderColor: '#CBD5E1',
                              },
                              '&:hover fieldset': {
                                borderColor: '#2563EB',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#2563EB',
                                boxShadow: '0 0 0 3px rgba(37,99,235,0.18)',
                              },
                            },
                            '& .MuiFormHelperText-root': {
                              ml: '14px',
                            },
                          }}
                        />
                        {status.message ? (
                          <Alert severity={status.type} sx={{ borderRadius: 3 }}>
                            {status.message}
                          </Alert>
                        ) : null}
                        <Button
                          variant="contained"
                          size="large"
                          type="button"
                          onClick={handleGetStarted}
                          disabled={isSubmitting}
                          fullWidth
                          sx={{
                            bgcolor: '#2563EB',
                            color: '#ffffff',
                            fontWeight: 700,
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '0.95rem',
                            boxShadow: '0 4px 16px rgba(37,99,235,0.35)',
                            transition: 'all 0.22s ease',
                            '&:hover': {
                              bgcolor: '#1D4ED8',
                              boxShadow: '0 6px 24px rgba(37,99,235,0.45)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          {isSubmitting ? (
                            <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                          ) : (
                            'Get started free'
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Fade>
                </Box>
              </Slide>
            </Grid>

            {/* ── Right: Lottie ──────────────────────────────────── */}
            <Grid item xs={12} md={6}>
              <Slide in={mounted} direction="left" timeout={800}>
                <Fade in={mounted} timeout={1000}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      maxWidth: { xs: 380, sm: 500, md: 920 },
                      mx: 'auto',
                      filter: 'drop-shadow(0 20px 48px rgba(37,99,235,0.12))',
                      animation: 'float 5s ease-in-out infinite',
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-12px)' },
                      },
                    }}
                  >
                    <DotLottieReact
                      src={checklistLottie}
                      loop
                      autoplay
                      style={{ width: '100%' }}
                    />
                  </Box>
                </Fade>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <Fade in={mounted} timeout={1200}>
        <Box
          component="footer"
          sx={{
            bgcolor: '#ffffff',
            borderTop: '1px solid #E2E8F0',
            py: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            © {new Date().getFullYear()} Lanzo. All rights reserved.
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
}
